import { FC, useCallback, useEffect, useState } from 'react';
import { Button } from '@material-ui/core';
import i18next from 'i18next';
import { useSnackbar } from 'notistack';
import { useDispatch } from 'react-redux';
import { useStyles } from './styles';
import { getAuthUrl, purchaseEvent } from '../../../../services/RallyService';
import RallyMessage from '../RallyMessage';
import { RESET_RALLY, SET_LOADING_RALLY } from '../../../../store/actions/Rally.actions';
import { RallyReducer } from '../../../../models/Redux';

interface Props {
    eventId: string;
    remainingSeats: number;
}

const RallyLogin: FC<Props> = ({ remainingSeats, eventId }) => {
    const classes = useStyles();
    const { enqueueSnackbar } = useSnackbar();
    const dispatch = useDispatch();

    const [isSuccessModalOpen, setIsSuccessModalOpen] = useState<boolean>(false);

    const redirectToRallyLogin = async () => {
        try {
            const rallyUrl = await getAuthUrl();

            let popupURL = rallyUrl;

            if (process.env.REACT_APP_IS_GOTMY) {
                console.log('REACT_APP_IS_GOTMY', process.env.REACT_APP_IS_GOTMY);
                popupURL = `${process.env.REACT_APP_OAUTH_GATEWAY}?origin=${window.location.protocol}//${
                    window.location.hostname
                }&rallyOAUTH=${encodeURIComponent(rallyUrl)}`;
            }

            const popupWindow = window.open(
                popupURL,
                '_blank',
                process.env.NODE_ENV === 'development' ? '' : 'menubar=0,resizable=0,width=840,height=940',
            );

            if (popupWindow) {
                popupWindow.focus();
            } else {
                enqueueSnackbar('Please allow popups for this website', { variant: 'error' });
            }
        } catch (e: any) {
            enqueueSnackbar(e.message, { variant: 'error' });
        }
    };

    const completePurchase = useCallback(
        async (code: string) => {
            try {
                dispatch({
                    type: SET_LOADING_RALLY,
                    payload: true,
                });

                await purchaseEvent({ event_uuid: eventId, code });
                setIsSuccessModalOpen(true);
            } catch (e: any) {
                if (e?.response?.data) {
                    enqueueSnackbar(
                        e.response.data?.error?.map(({ message }: { message: string }) => message).join(','),
                        { variant: 'error' },
                    );
                } else {
                    enqueueSnackbar(e.message, { variant: 'error' });
                }
            } finally {
                dispatch({
                    type: RESET_RALLY,
                });
            }
        },
        [dispatch, enqueueSnackbar, eventId],
    );

    useEffect(() => {
        dispatch({ type: RESET_RALLY });
    }, [dispatch]);

    useEffect(() => {
        const checkRallyInterval = setInterval(() => {
            const rallyState: RallyReducer = JSON.parse(
                JSON.parse(localStorage.getItem('persist:GotMy') || '{}').rally || '{}',
            );

            if (rallyState.code && !rallyState.loading && !rallyState.error) {
                clearInterval(checkRallyInterval);
                completePurchase(rallyState.code);
            }
        }, 500);
    }, [completePurchase, dispatch, enqueueSnackbar]);

    return (
        <>
            <Button
                className={classes.button}
                onClick={remainingSeats > 0 ? redirectToRallyLogin : undefined}
            >
                {remainingSeats > 0
                    ? i18next.t('EventPageComponentGetTicketWithRally')
                    : i18next.t('EventPageComponentSorryEventSoldOut')}
            </Button>

            <RallyMessage isOpen={isSuccessModalOpen} onClose={setIsSuccessModalOpen} />
        </>
    );
};

export default RallyLogin;
